import { ConfigProvider, Menu, Tooltip, theme as AntTheme } from 'antd';
import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';
import { useNumAccounts } from '@copilot/common/pages/accountDashboard/data/hook';
import { isNil } from 'lodash';

type SidebarTooltipPropsType = {
	/**
	 * Tooltip's title
	 */
	title: string;
	/**
	 * The element that the tooltip is for
	 */
	children: ReactNode;
};

/**
 * Creates a sidebar tooltip component with a given title and children.
 */
function SidebarTooltip(props: SidebarTooltipPropsType): JSX.Element {
	const { title, children } = props;
	return (
		<Tooltip
			title={title}
			placement={'right'}
			overlayInnerStyle={{ width: 'fit-content', whiteSpace: 'nowrap' }}
		>
			{children}
		</Tooltip>
	);
}

const Paths = {
	AccountDashboard: '/dashboard',
} as const;

/**
 * Represents an Account Dashboard Sidebar component.
 * It is a functional component that renders a sidebar menu for an account dashboard.
 *
 * @return {JSX.Element} Returns the rendered sidebar menu component.
 */
export function AccountDashboardSidebar(): JSX.Element {
	const { pathname } = useLocation();
	const history = useHistory();
	const { token } = AntTheme.useToken();
	const { data, loading } = useNumAccounts();
	const items = [
		{
			label: (
				<SidebarTooltip
					title={"Manage your customers' account settings and monitor their campaigns"}
				>
					<div style={{ paddingInline: token.padding }}>
						Customer Account {!loading && !isNil(data) && `(${data})`}
					</div>
				</SidebarTooltip>
			),
			onClick: () => history.push(Paths.AccountDashboard),
			key: Paths.AccountDashboard,
		},
	];

	return (
		<ConfigProvider
			theme={{
				// The tooltip only show if we hover over the label.
				// Therefore we need to make sure the label is the same size as the menu item
				token: {
					padding: 0,
				},
			}}
		>
			<Menu
				style={{ userSelect: 'none', border: 'none' }}
				selectedKeys={[pathname]}
				items={items}
			/>
		</ConfigProvider>
	);
}
